import type {FC} from 'react';
import {LandingParagraph} from '@tempo/landing';
import {
	AppleStoreBadge,
	Heading,
	Link,
	PlayStoreBadge,
	Stack,
	Text,
} from '@tempo/core';
import {useTranslation} from '@app/hooks/use-translation';
import {useRouter} from 'next/router';
import {Image} from '@app/components/Image';
import type {TranslationKey} from 'globalTranslations';

import {tempo} from '@tempo/core';
import {useUser} from '@deezer/react-user';
import {LandingIllustrationSection} from '@app/components/layout/LandingIllustrationSection';
import type {DeviceContentConfig} from '@app/utils';

export const TextWithLink = tempo(Text);
TextWithLink.defaultProps = {
	textAlign: {base: 'center', md: 'left'},
	color: 'text.neutral.secondary.default',
	sx: {
		a: {
			color: 'text.accent.primary.default',
		},
		'a:hover': {
			color: 'text.accent.primary.hovered',
		},
	},
};

type Props = {
	configs: DeviceContentConfig[];
};

export const DevicesContent: FC<Props> = ({configs}) => {
	const {t} = useTranslation({ns: ['devices']});
	const [user] = useUser();
	const {locale} = useRouter();

	return (
		<>
			{configs
				.filter(
					(config) =>
						!(
							config.displayCountry &&
							!config.displayCountry.includes(user?.country as string)
						),
				)
				.map((config, index) => {
					const {key: titleKey, ...titleOption} = config.title;
					const {
						key: descriptionKey,
						hasLink,
						...descriptionOption
					} = config.description;
					const title = t(titleKey as TranslationKey, titleOption);
					let description;

					if (typeof descriptionKey === 'string') {
						description = t(
							descriptionKey as TranslationKey,
							descriptionOption,
						);
					} else if (Array.isArray(descriptionKey)) {
						description = descriptionKey
							.map((key) => t(key, descriptionOption))
							.join(' ');
					} else {
						description = descriptionKey;
					}

					const subDescription = config.subDescription
						? t(config.subDescription.key as TranslationKey, {
								...config.subDescription,
							})
						: undefined;

					return (
						<LandingIllustrationSection
							key={`${index}-${title}`}
							imagePosition={index % 2 === 0 ? 'left' : 'right'}
							imageContained
							description={
								<>
									<Stack direction="row" alignItems="center" gap="size.xs">
										<Heading as="h2" variant="heading.m">
											{title}
										</Heading>
										{config.withHifi && (
											<Link href="/explore/features/hifi">HiFi</Link>
										)}
									</Stack>

									{config.subTitle && (
										<Heading as="h2" variant="heading.m">
											{t(config.subTitle.key as TranslationKey)}
										</Heading>
									)}

									{hasLink ? (
										<TextWithLink
											dangerouslySetInnerHTML={{
												__html: description,
											}}
										/>
									) : (
										<LandingParagraph>{description}</LandingParagraph>
									)}

									{config.subDescription && (
										<Text
											fontSize="13px"
											color="text.neutral.secondary.default"
											mt=".5rem !important"
										>
											{subDescription}
										</Text>
									)}

									{config.brandImage && (
										<Image
											priority={index === 0}
											aspectRatio={config.brandImage.aspectRatio}
											src={config.brandImage.src}
											alt={t(config.brandImage.alt as TranslationKey)}
											w="100px"
											h="20px"
											style={{
												width: '100px',
												height: '20px',
											}}
										/>
									)}

									{config.withAndroidBadge && (
										<PlayStoreBadge
											data-cy="google-play-store"
											target="_blank"
											locale={locale}
										/>
									)}

									{config.withIosBadge && (
										<AppleStoreBadge
											data-cy="app-store"
											target="_blank"
											locale={locale}
										/>
									)}

									{config.learnMoreLink && (
										<Link
											href={
												config.pathToDisplay === 'teufel'
													? `https://www.teufel.${user?.country?.toLowerCase()}/deezer.html`
													: config.learnMoreLink
											}
											variant="deezer"
											target="_blank"
										>
											{t('En savoir plus')}
										</Link>
									)}
								</>
							}
							image={
								<Image
									priority={index === 0}
									justifyContent={{base: 'center', md: 'flex-end'}}
									w="480px"
									h="330px"
									aspectRatio={config.image.aspectRatio}
									src={config.image.src}
									alt={t(config.image.alt as TranslationKey)}
								/>
							}
						/>
					);
				})}
		</>
	);
};
